import React from 'react';
import { ProfilePicture } from '@getro/rombo';
import { Flex, Box, Text, Link } from 'rebass/styled-components';
import PropTypes from 'prop-types';

import { sortPositionsByCompany } from '../../../../../services/contacts/postitions';
import { CompanyInfo } from '../../companyInfo';
import { ContactInfoWorkDate } from '../../contactInfoWorkDate';
import { EnrichmentButton } from '../../enrichmentButton';
import { formatYearRange } from '../../../../../services/dateFormatter';

export const Experience = ({ pastPositions, education, skills, contact, networkId, list, type }) => {
  const sortedPastPositions = sortPositionsByCompany(pastPositions);

  if (contact?.enrichmentStatus !== 'enriched' && type !== 'public') {
    return (
      <Box sx={{ padding: '16px', borderRadius: '4px', backgroundColor: 'neutral.20' }}>
        <EnrichmentButton contact={contact} networkId={networkId} isList={Boolean(list?.id)} isContactDetail />
      </Box>
    );
  }

  return (
    <>
      <Text
        mt="8px"
        fontSize="0"
        fontWeight="medium"
        color="text.subtle"
        sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}
      >
        Work experience
      </Text>
      {sortedPastPositions?.length ? (
        <Flex mt="8px" flexDirection="column" sx={{ gap: '24px' }}>
          {sortedPastPositions.map((companyPositions) => (
            <Flex
              key={`past-experience-dv-${companyPositions[0].companyName}-${companyPositions[0].companyLinkedinUrl}`}
            >
              <Box mr="2" flex="0 0 auto" width="32px" height="32px">
                {companyPositions[0].companyLinkedinUrl ? (
                  <Link
                    href={companyPositions[0].companyLinkedinUrl}
                    sx={{ textDecoration: 'none', ':hover': { textDecoration: 'underline' } }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ProfilePicture
                      name={companyPositions[0].companyName || ''}
                      variant="square"
                      imageUrl={companyPositions[0].logoUrl}
                    />
                  </Link>
                ) : (
                  <ProfilePicture
                    name={companyPositions[0].companyName || ''}
                    variant="square"
                    imageUrl={companyPositions[0].logoUrl}
                  />
                )}
              </Box>
              {companyPositions.length === 1 && (
                <Box>
                  <Text fontSize="14px" fontWeight="medium" color="text.dark">
                    {companyPositions[0].jobTitle}
                  </Text>
                  <CompanyInfo position={companyPositions[0]} />
                  {companyPositions[0].dateFrom && <ContactInfoWorkDate mt="4px" position={companyPositions[0]} />}
                </Box>
              )}
              {companyPositions.length !== 1 && (
                <Box>
                  <Flex alignItems="center" sx={{ gap: '8px' }}>
                    <CompanyInfo position={companyPositions[0]} />
                  </Flex>
                  {companyPositions.map((pos, index) => (
                    <Box
                      mt={index === 0 ? '8px' : '4px'}
                      key={`past-experience-dv-${companyPositions[0].companyName}-${pos.jobTitle}`}
                    >
                      <Text fontSize="1" fontWeight="medium" color="text.dark">
                        {pos.jobTitle}
                      </Text>
                      {pos.dateFrom && <ContactInfoWorkDate position={pos} />}
                    </Box>
                  ))}
                </Box>
              )}
            </Flex>
          ))}
        </Flex>
      ) : (
        <Text mt="3" fontSize="1" color="text.subtle" fontStyle="italic">
          No current or past roles available from LinkedIn.
        </Text>
      )}

      <Text
        mt="32px"
        mb="8px"
        fontSize="0"
        fontWeight="medium"
        color="text.subtle"
        sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}
      >
        Education
      </Text>
      {education?.length ? (
        <Flex flexDirection="column" sx={{ gap: '24px' }}>
          {education.map((edu) => (
            <Flex key={`education-${edu.id}`}>
              <Box mr="2" flex="0 0 auto" width="32px" height="32px">
                <ProfilePicture name={edu.school?.name || ''} variant="square" imageUrl={edu.school?.logoUrl} />
              </Box>
              <Box>
                {edu.school?.name && (
                  <Text fontSize="14px" fontWeight="medium" color="text.dark">
                    {edu.school.name}
                  </Text>
                )}
                {(edu.degree?.length > 0 || edu.major?.length > 0) && (
                  <Text fontSize="14px" color="text.main">
                    {edu.degree?.length > 0 && edu.degree.join(', ')}
                    {edu.degree?.length > 0 && edu.major?.length > 0 && ', '}
                    {edu.major?.length > 0 && edu.major.join(', ')}
                  </Text>
                )}
                {(edu.dateFrom || edu.dateTo) && (
                  <Text mt="4px" fontSize="14px" color="text.subtle">
                    {formatYearRange(edu.dateFrom, edu.dateTo)}
                  </Text>
                )}
                {edu.description?.length > 0 && (
                  <Text fontSize="14px" color="text.main">
                    {edu.description}
                  </Text>
                )}
                {edu.grades?.length > 0 && (
                  <Text fontSize="14px" color="text.main">
                    {edu.grades}
                  </Text>
                )}
              </Box>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Text fontSize="1" color="text.subtle" fontStyle="italic">
          No education details available from LinkedIn.
        </Text>
      )}

      <Text
        mt="32px"
        mb="8px"
        fontSize="0"
        fontWeight="medium"
        color="text.subtle"
        sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}
      >
        Skills
      </Text>
      {skills?.length ? (
        <Text fontSize="1" lineHeight="24px" sx={{ textTransform: 'capitalize' }}>
          {skills.join(', ')}
        </Text>
      ) : (
        <Text fontSize="1" color="text.subtle" fontStyle="italic">
          No skills available from LinkedIn.
        </Text>
      )}
    </>
  );
};

Experience.propTypes = {
  pastPositions: PropTypes.array,
  education: PropTypes.array,
  skills: PropTypes.array,
  contact: PropTypes.object,
  networkId: PropTypes.string,
  list: PropTypes.object,
  type: PropTypes.string,
};

Experience.defaultProps = {
  pastPositions: [],
  education: [],
  skills: [],
  contact: {},
  networkId: '',
  list: {},
  type: '',
};
